import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createFeature, getFeatureByID, editFeature } from '../../store/actions/feature/feature.action';
import CreateFeature from '../../components/Features/CreateFeature';
import PageLoader from '../../components/Common/PageLoader';

const CreateFeaturesContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isCreating = useSelector((state) => state.feature.isCreating);
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [featureToBeEdit, setFeatureToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const featureCategory = categories.find((category) => category.link === 'features');
      if (featureCategory) {
        setAccessMode(featureCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('Feature category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Get feature for edit mode
  const getFeature = useCallback(async (featureID) => {
    try {
      setIsLoading(true);
      const feature = await dispatch(getFeatureByID(featureID));
      setFeatureToBeEdit(feature);
    } catch (error) {
      console.error('Error fetching feature:', error);
      navigate('/features');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate]);

  // Handle create feature
  const handleCreate = useCallback((values) => {
    dispatch(createFeature(values));
  }, [dispatch]);

  // Handle edit feature
  const handleEdit = useCallback((values) => {
    dispatch(editFeature({ ...values, id: params.id }));
  }, [dispatch, params.id]);

  // Initialize on mount
  useEffect(() => {
    // Set mode based on params
    setMode(params.id ? 'EDIT' : 'CREATE');

    // Set access permissions
    setAccess();

    // Fetch feature if in edit mode
    if (params.id) {
      getFeature(params.id);
    }
  }, [params.id, getFeature, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state for edit mode
  if ((mode === 'EDIT' && !featureToBeEdit) || isLoading) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return (
      <div className="alert alert-danger" role="alert">
        This category is not accessible to the logged in user.
      </div>
    );
  }

  return (
    <section>
      <CreateFeature
        accessMode={accessMode}
        onCreate={handleCreate}
        onEdit={handleEdit}
        initialValues={featureToBeEdit}
        mode={mode}
        isCreating={isCreating}
      />
    </section>
  );
};

export default CreateFeaturesContainer;
